import { ImageAnalysisResult } from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

const CRAFTSMAN_COMPONENTS: { key: keyof ImageAnalysisResult['content']; label: string }[] = [
  { key: 'overall_class', label: 'Overall' },
  { key: 'floor_structure_class', label: 'Floor Structure' },
  { key: 'wall_framing_and_exterior_finish_class', label: 'Wall / Exterior Finish' },
  { key: 'roof_class', label: 'Roof' },
  { key: 'floor_finish_class', label: 'Floor Finish' },
  { key: 'interior_wall_and_ceiling_finish_class', label: 'Interior Wall & Ceiling' },
  { key: 'interior_detail_class', label: 'Interior Detail' },
  { key: 'bath_detail_class', label: 'Bath Detail' },
  { key: 'kitchen_detail_class', label: 'Kitchen Detail' },
  { key: 'plumbing_detail_class', label: 'Plumbing' },
  { key: 'electrical_system_class', label: 'Electrical' },
  { key: 'special_features_class', label: 'Special Features' },
];

const CRAFTSMAN_FEATURES: { key: keyof ImageAnalysisResult['content']; label: string }[] = [
  { key: 'has_pool', label: 'Pool' },
  { key: 'has_fireplace', label: 'Fireplace' },
  { key: 'has_barn', label: 'Barn' },
  { key: 'has_carport', label: 'Carport' },
  { key: 'has_basement', label: 'Basement' },
  { key: 'has_deck', label: 'Deck' },
  { key: 'has_covered_porch', label: 'Covered Porch' },
  { key: 'has_enclosed_patio', label: 'Enclosed Patio' },
  { key: 'has_accessory_dwelling_unit', label: 'ADU' },
  { key: 'has_shed', label: 'Shed' },
  { key: 'has_workshop', label: 'Workshop' },
];

const commentaryKey = (classKey: string) => classKey.replace(/_class$/, '_commentary');

export const CraftsmanResults = ({ data }: { data: ImageAnalysisResult }) => {
  const c = data.content;
  const weights = data._debug?.componentWeights ?? {};
  return (
    <Flex flexDirection="column" gap="16px">
      {/* UAD condition row */}
      {(c.uad_condition_rating != null || c.uad_condition_commentary != null) && (
        <Block padding="12px 16px" borderRadius="4px" backgroundColor="surface2">
          <Flex gap="12px" alignItems="baseline">
            <Typography variant="h4">UAD Condition</Typography>
            {c.uad_condition_rating != null && (
              <Typography variant="h4">{c.uad_condition_rating}</Typography>
            )}
          </Flex>
          {c.uad_condition_commentary != null && (
            <Typography variant="body2" color="textSecondary">
              {c.uad_condition_commentary}
            </Typography>
          )}
        </Block>
      )}

      {/* Per-component table */}
      <div style={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        {CRAFTSMAN_COMPONENTS.map(({ key, label }, i) => {
          const classVal = c[key] as number | undefined;
          const commentary = c[commentaryKey(key) as keyof typeof c] as string | undefined;
          const isOverall = key === 'overall_class';
          return (
            <Flex
              key={key}
              padding="10px 16px"
              gap="16px"
              alignItems="flex-start"
              backgroundColor={i % 2 === 0 ? 'surface1' : 'surface2'}
            >
              <div style={{ minWidth: 220 }}>
                <Typography variant={isOverall ? 'h4' : 'body2'}>{label}</Typography>
              </div>
              <div style={{ minWidth: 80 }}>
                <Typography variant={isOverall ? 'h4' : 'body2'}>
                  {classVal != null ? classVal : '—'}
                  {!isOverall && weights[key] != null ? ` (weight: ${weights[key]})` : ''}
                </Typography>
              </div>

              <div style={{ flex: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  {commentary ?? '—'}
                </Typography>
              </div>
            </Flex>
          );
        })}
      </div>

      {/* Features */}
      <Flex gap="8px" flexWrap="wrap">
        {CRAFTSMAN_FEATURES.map(({ key, label }) => {
          const val = c[key] as boolean | undefined;
          if (val == null) return null;
          return (
            <Block
              key={key}
              padding="4px 10px"
              borderRadius="12px"
              backgroundColor={val ? 'selected1' : 'surface2'}
            >
              <Typography variant="body2" color={val ? 'textPrimary' : 'textSecondary'}>
                {label}
              </Typography>
            </Block>
          );
        })}
      </Flex>
    </Flex>
  );
};
